import type { AiqTriangleReport } from '../../types/aiq-report'
import { TriangleChart, type TriangleScorePoint } from './TriangleChart'

const DOMAIN_LABELS: Record<string, string> = {
  abstraction: '무시',
  change: '변화',
  relation: '관계',
}

const DOMAIN_DESCRIPTIONS: Record<string, string> = {
  abstraction: '불필요한 요소를 무시하고 핵심을 파악하는 능력',
  change: '변환과 변화를 인식하고 표현하는 능력',
  relation: '개념 간 관계를 파악하고 연결하는 능력',
}

const PRE_COLOR = '#3b82f6'  // blue
const POST_COLOR = '#8b5cf6' // purple

interface AiqTriangleSectionProps {
  report: AiqTriangleReport
  type: 'standard' | 'advanced'
  phase: 'pre' | 'post' | 'comparison'
}

export function AiqTriangleSection({ report, phase }: AiqTriangleSectionProps) {
  // Standard/Advanced 모두 동일한 0-100 스케일로 시각화.
  // Advanced scoring 단계에서는 2.0 cap(최대 200)이 유지되지만, 100 초과값은
  // TriangleChart 내부에서 Math.min(score, maxScore)로 꼭짓점에 clamp된다.
  const maxScore = 100
  const { thresholds } = report

  // Build domain scores for the selected phase
  const buildDomainScores = (): TriangleScorePoint[] => {
    const domains = ['abstraction', 'change', 'relation'] as const

    if (phase === 'comparison') {
      return domains.map((d) => {
        const preVal = report.preDomainScores?.[d] ?? 0
        const postVal = report.postDomainScores?.[d] ?? 0
        return {
          label: DOMAIN_LABELS[d],
          value: preVal,
          color: PRE_COLOR,
          comparisonValue: postVal,
          comparisonColor: POST_COLOR,
        }
      })
    }

    const scores = phase === 'pre' ? report.preDomainScores : report.postDomainScores
    const color = phase === 'pre' ? PRE_COLOR : POST_COLOR
    return domains.map((d) => ({
      label: DOMAIN_LABELS[d],
      value: scores?.[d] ?? 0,
      color,
    }))
  }

  // Build overall score
  const buildOverallScores = (): TriangleScorePoint[] => {
    if (phase === 'comparison') {
      return [{
        label: '전체',
        value: report.preOverallScore ?? 0,
        color: PRE_COLOR,
        comparisonValue: report.postOverallScore ?? 0,
        comparisonColor: POST_COLOR,
      }]
    }

    const val = phase === 'pre' ? report.preOverallScore : report.postOverallScore
    const color = phase === 'pre' ? PRE_COLOR : POST_COLOR
    return [{ label: '전체', value: val ?? 0, color }]
  }

  // Improvement annotations for comparison
  const getImprovementAnnotations = () => {
    if (phase !== 'comparison') return null
    const annotations: string[] = []

    const domains = ['abstraction', 'change', 'relation'] as const
    for (const d of domains) {
      const pre = report.preDomainScores?.[d] ?? 0
      const post = report.postDomainScores?.[d] ?? 0
      if (post > pre) {
        const diff = Math.round(post - pre)
        annotations.push(`${DOMAIN_LABELS[d]} 능력이 ${diff}점 향상되었습니다! 🎉`)
      }
    }

    const preTotal = report.preOverallScore ?? 0
    const postTotal = report.postOverallScore ?? 0
    if (postTotal > preTotal) {
      const diff = Math.round(postTotal - preTotal)
      annotations.push(`전체 점수가 ${diff}점 향상! 꾸준한 학습의 성과입니다.`)
    }

    return annotations
  }

  const annotations = getImprovementAnnotations()

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-6 justify-center">
        {/* Domain triangle */}
        <TriangleChart
          scores={buildDomainScores()}
          thresholds={thresholds}
          title="분야별 점수"
          maxScore={maxScore}
          showArrows={phase === 'comparison'}
          width={220}
          height={280}
        />

        {/* Overall triangle */}
        <TriangleChart
          scores={buildOverallScores()}
          thresholds={thresholds}
          title="전체 점수"
          maxScore={maxScore}
          showArrows={phase === 'comparison'}
          width={160}
          height={280}
        />
      </div>

      {/* Legend */}
      {phase === 'comparison' && (
        <div className="flex items-center justify-center gap-6 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: PRE_COLOR }} />
            Pre-AIQ
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: POST_COLOR }} />
            Post-AIQ
          </span>
        </div>
      )}

      {/* Improvement annotations */}
      {annotations && annotations.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1">
          {annotations.map((a, i) => (
            <p key={i} className="text-sm text-green-700">{a}</p>
          ))}
        </div>
      )}

      {/* Domain descriptions */}
      <div className="grid grid-cols-3 gap-2 text-xs text-gray-500">
        {(['abstraction', 'change', 'relation'] as const).map((d) => (
          <div key={d} className="text-center">
            <span className="font-semibold text-gray-700">{DOMAIN_LABELS[d]}</span>
            <p className="mt-0.5">{DOMAIN_DESCRIPTIONS[d]}</p>
          </div>
        ))}
      </div>
    </div>
  )
}
